import React from 'react'
import {Box, Typography} from '@mui/material'
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined'
import DatePickerNew from 'src/components-new/inputs/DatePickerNew'
import TimePickerNew from 'src/components-new/inputs/TimePickerNew'
import {useAppSelector} from 'src/hooks'
import {NEUTRAL_SCALE} from 'src/theme-new/tokens'
import {
  getBodyPartSuggestions,
  getAssumptionColumnOptions,
  getVisibleAssumptionFieldIds,
  SecondaryAssumption,
  SecondaryAssumptionResponseForm
} from 'src/constants/secondaryAssumptions'
import AssumptionTakenChoiceNew from './AssumptionTakenChoiceNew'
import SectionTitleNew from './SectionTitleNew'
import BodyPartPickerNew from './BodyPartPickerNew'

// The answer form itself, controlled by its parent so the same markup serves both surfaces that
// can answer a reminder: the deep-link page reached from the mail/SMS, and the dialog opened from
// a row of the assumptions list (answering an old reminder is explicitly allowed — RF4.2).

interface AssumptionResponseFormNewProps {
  assumption: SecondaryAssumption
  form: SecondaryAssumptionResponseForm
  onChange: (form: SecondaryAssumptionResponseForm) => void
  disabled?: boolean
  /**
   * Skips the "Hai assunto la terapia?" question — for the quick-confirm dialog opened by the
   * list's round ✓ button, where the answer is already implied by that tap. The caller is
   * responsible for having set `form.taken` to 'yes' before rendering; this only hides the
   * question, it does not itself decide the value.
   */
  hideTakenQuestion?: boolean
  /**
   * Fired right after the ✓/✗ choice is made, with the resulting form — for the deep-link page,
   * where tapping the round button IS the answer and a separate "Invia risposta" click on top of
   * it would be redundant friction (the tap already carries a complete, submittable answer: "no"
   * needs nothing else, and "yes" defaults to "taken as planned"). Carries the next form rather
   * than just the value because `onChange` above only schedules a state update — reading `form`
   * from the closure right after calling it would still see the pre-tap value.
   *
   * Deliberately NOT wired by the "Modifica"/quick-confirm dialogs: those have an explicit
   * Annulla/Conferma contract, and auto-submitting on tap would make "Annulla" lie after an
   * accidental tap. The deep-link page has no such contract — the page itself is the action, and
   * RF4.2 makes any answer correctable afterward regardless.
   */
  onTakenChoice?: (form: SecondaryAssumptionResponseForm) => void
  /**
   * Shows the details of this dose — date, time and body part — as their own "Questa assunzione"
   * section right below the ✓/✗ question, always visible instead of only after a "yes": for the
   * deep-link page, which must show at a glance both this dose and the next one. Date/time start
   * from the expected date; the whole section is read-only once the answer is "no".
   */
  alwaysShowDetails?: boolean
}

export default function AssumptionResponseFormNew({
  assumption,
  form,
  onChange,
  disabled,
  hideTakenQuestion,
  onTakenChoice,
  alwaysShowDetails
}: AssumptionResponseFormNewProps): JSX.Element {
  const assumptionsColumnOptions = useAppSelector(
    state => getAssumptionColumnOptions(state.pspConfigReducer.data)
  )
  const visibleFields = getVisibleAssumptionFieldIds(assumptionsColumnOptions)
  const configuredBodyParts = useAppSelector(
    state => state.pspConfigReducer.data.therapyDiaryConfigurations?.secondaryTherapiesConfiguration?.bodyParts
  )
  const bodyPartSuggestions = getBodyPartSuggestions(configuredBodyParts)
  const showDate = visibleFields.has('date')
  const showTime = visibleFields.has('time')
  const showBodyPart = visibleFields.has('bodyPart')
  const showTiming = showDate || showTime

  const onTakenChange = (value: 'yes' | 'no'): void => {
    const nextForm = {...form, taken: value}
    onChange(nextForm)
    onTakenChoice?.(nextForm)
  }

  // CANCELLED answers carry no takenAt: the planned time is still what the section shows
  const takenAtValue = form.takenAt ?? assumption.expectedDate ?? null
  const detailsDisabled = disabled || form.taken === 'no'

  const renderBodyPart = (fieldsDisabled?: boolean): JSX.Element => (
    <BodyPartPickerNew
      value={form.bodyPart}
      onChange={(value) => onChange({...form, bodyPart: value})}
      suggestions={bodyPartSuggestions}
      disabled={fieldsDisabled}
    />
  )

  return (
    <Box sx={{display: 'flex', flexDirection: 'column', gap: 3}}>
      {!hideTakenQuestion && (
        <Box>
          <Typography
            variant="caption"
            sx={{fontWeight: 600, color: NEUTRAL_SCALE[700], textTransform: 'uppercase', letterSpacing: '0.05em', mb: 1.5, display: 'block'}}
          >
            Hai assunto la terapia?
          </Typography>
          <AssumptionTakenChoiceNew value={form.taken} onChange={onTakenChange} disabled={disabled} />
        </Box>
      )}

      {alwaysShowDetails && (
        <Box>
          <SectionTitleNew icon={EventAvailableOutlinedIcon}>Questa assunzione</SectionTitleNew>
          <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', sm: '1fr 1fr'}, gap: 2}}>
            <DatePickerNew
              label="Data"
              required
              fullWidth
              disabled={detailsDisabled}
              value={takenAtValue}
              onChange={(value) => onChange({...form, takenAt: value})}
            />
            <TimePickerNew
              label="Ora"
              required
              fullWidth
              disabled={detailsDisabled}
              value={takenAtValue}
              onChange={(value) => onChange({...form, takenAt: value})}
            />
          </Box>
          {showBodyPart && <Box sx={{mt: 3}}>{renderBodyPart(detailsDisabled)}</Box>}
        </Box>
      )}

      {!alwaysShowDetails && form.taken === 'yes' && (
        <>
          {showTiming && (
            <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', sm: '1fr 1fr'}, gap: 2}}>
              {showDate && (
                <DatePickerNew
                  label="Data"
                  required
                  fullWidth
                  disabled={disabled}
                  value={form.takenAt}
                  onChange={(value) => onChange({...form, takenAt: value})}
                />
              )}
              {showTime && (
                <TimePickerNew
                  label="Ora"
                  required
                  fullWidth
                  disabled={disabled}
                  value={form.takenAt}
                  onChange={(value) => onChange({...form, takenAt: value})}
                />
              )}
            </Box>
          )}

          {showBodyPart && renderBodyPart(disabled)}
        </>
      )}
    </Box>
  )
}
